import React, { Component } from "react"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import UserVisibleErrorStoringDecorator from "src/components/decorators/UserErrorStoringDecorator"
import SearchIcon from "src/components/search/SearchIcon"
import ApiHandler from "src/api/ApiHandler"
import QuotaLimitExceededError from "src/validation/exception_messages/QuotaLimitExceededError"
import navigationService from "src/navigation/NavigationService"
import posthog from "src/consent/posthog"

interface SearchFormState {
    address: string
    suggestions: any[]
    showSuggestions: boolean
    loading: boolean
    quotaLimitReached: boolean
    border: boolean
}

export default class SearchForm extends Component<any, SearchFormState> {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    private debounceTimeout: NodeJS.Timeout | null = null
    private static ADDRESS_LENGTH_TO_HIDE_AUTOSUGGEST_QUOTA_LIMIT_MESSAGE = 9

    constructor(props: any) {
        super(props)
        this.stateHandler = props.stateHandler
        this.apiHandler = props.apiHandler
        this.search = this.search.bind(this)
        this.handleInputChange = this.handleInputChange.bind(this)
        this.handleSuggestionClick = this.handleSuggestionClick.bind(this)
        this.handleInputBlur = this.handleInputBlur.bind(this)
        this.searchAddresses = this.searchAddresses.bind(this)

        this.state = {
            address: this.stateHandler.get(StateConstants.ADDRESS_STATE) || "",
            suggestions: [],
            showSuggestions: false,
            loading: false,
            quotaLimitReached: false,
            border: props.border
        }

        this.stateHandler.addListener(StateConstants.ADDRESS_STATE, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                address: state
            }))
        })
        this.stateHandler.addListener(StateConstants.AUTOSUGGEST_QUOTA_LIMIT_REACHED, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                quotaLimitReached: state
            }))
        })
        this.stateHandler.addListener(StateConstants.SUGGESTIONS_STATE, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                suggestions: state
            }))
        })
        this.stateHandler.addListener(StateConstants.IS_LOADING, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                loading: state
            }))
        })
        this.stateHandler.addListener(StateConstants.SHOW_SUGGESTIONS, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                showSuggestions: state
            }))
        })
    }

    async performSearch() {
        this.stateHandler.set(StateConstants.SUGGESTIONS_STATE, [])
        this.stateHandler.set(StateConstants.SHOW_SUGGESTIONS, false)

        let address = this.stateHandler.get(StateConstants.ADDRESS_STATE)
        if (address === undefined || address === "") {
            return
        }

        let input = {"address": address}
        let foundAddress = await this.apiHandler.findAddress(input)
        let id = foundAddress?.["gi"]
        if (!id) {
            throw new Error(`No address found for '${address}'`)
        }

        posthog?.capture('address_search_submitted', { address })

        this.clearAddressField()
        await this.redirectToValuationPage(id)
    }
    
    clearAddressField() {
        this.setState({ address: "" })
        this.stateHandler.set(StateConstants.ADDRESS_STATE, "")
        // Clear suggestions as well
        this.stateHandler.set(StateConstants.SUGGESTIONS_STATE, [])
        this.stateHandler.set(StateConstants.SHOW_SUGGESTIONS, false)
    }

    @UserVisibleErrorStoringDecorator()
    async search(event: any) {
        event.preventDefault();
        await this.performSearch()
    }

    async redirectToValuationPage(id: string) {
        await navigationService.navigateToValuationRoute(
            id,
            this.apiHandler,
            this.props.valuationRouteBase || "/valuate"
        )
        // Keep IS_LOADING true through the route transition so the loader stays visible
        // until this component unmounts. The finally block in the decorator will set it
        // to false, but we immediately re-set it to prevent the search page background
        // from flashing before unmount.
        this.stateHandler.set(StateConstants.IS_LOADING, true)
    }

    handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value
        this.setState({ address: value, quotaLimitReached: false })
        this.stateHandler.set(StateConstants.ADDRESS_STATE, value)

        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout)
        }

        if (value.length >= 3) {
            this.setState({ loading: true })
            this.debounceTimeout = setTimeout(() => {
                this.searchAddresses(value)
            }, 300)
        } else {
            this.setState({ suggestions: [], showSuggestions: false, loading: false, quotaLimitReached: false })
        }
    }

    async searchAddresses(searchTerm: string) {
        try {
            const input = { "address": searchTerm }
            const response = await this.apiHandler.suggestAddress(input)
            const suggestions = response.suggestions || []
            const filteredSuggestions = suggestions.filter((suggestion: any) => suggestion && suggestion.a)

            this.setState({
                suggestions: filteredSuggestions,
                showSuggestions: filteredSuggestions.length > 0,
                loading: false
            })
            this.stateHandler.set(StateConstants.SUGGESTIONS_STATE, filteredSuggestions)
            this.stateHandler.set(StateConstants.SHOW_SUGGESTIONS, filteredSuggestions.length > 0)
            this.stateHandler.set(StateConstants.IS_LOADING, false)
        } catch (error) {
            console.error('Error searching addresses:', error)
            this.setState({ loading: false, showSuggestions: false })

            if (error instanceof QuotaLimitExceededError) {
                this.stateHandler.set(StateConstants.AUTOSUGGEST_QUOTA_LIMIT_REACHED, true)
            } else {
                this.stateHandler.set(StateConstants.AUTOSUGGEST_QUOTA_LIMIT_REACHED, false)
            }
        }
    }

    handleSuggestionClick(suggestion: any) {
        const address = suggestion.a || suggestion.full_address || suggestion.address
        posthog?.capture('address_suggestion_selected', { address })
        this.stateHandler.set(StateConstants.SUGGESTIONS_STATE, [])
        this.stateHandler.set(StateConstants.ADDRESS_STATE, address)
        this.stateHandler.set(StateConstants.SHOW_SUGGESTIONS, false)
        // Note: performSearch will clear the address field after successful search
        void this.performSearch()
    }

    handleInputBlur() {
        setTimeout(() => {
            this.stateHandler.set(StateConstants.SHOW_SUGGESTIONS, false)
        }, 200)
    }

    shouldShowQuotaLimitMessage(): boolean {
        const address = this.state.address
        if (!address || address.length === 0) return false
        return address.length < SearchForm.ADDRESS_LENGTH_TO_HIDE_AUTOSUGGEST_QUOTA_LIMIT_MESSAGE
    }

    render() {
        let borderClass = this.props.border ? "valuations" : ""
        return (
            <form className="search-form" onSubmit={this.search}>
                <fieldset className={"address-fieldset " + borderClass + " container"}>
                    {!this.props.showSubmitButton && <SearchIcon></SearchIcon>}
                    <div className="address-input-container">
                        <input
                            type="text"
                            className="field address-field"
                            placeholder={this.props.placeholder || "Search an address"}
                            id="valuation-form-street-address"
                            name="address"
                            value={this.state.address}
                            onChange={this.handleInputChange}
                            onBlur={this.handleInputBlur}
                            autoComplete="off"
                            data-1p-ignore="true"
                            data-lpignore="true"
                            data-bwignore="true"
                        ></input>
                        {this.stateHandler.get(StateConstants.IS_LOADING) && !this.stateHandler.get(StateConstants.AUTOSUGGEST_QUOTA_LIMIT_REACHED) && (
                            <div className="loading-indicator"></div>
                        )}
                        {this.stateHandler.get(StateConstants.AUTOSUGGEST_QUOTA_LIMIT_REACHED) && this.shouldShowQuotaLimitMessage() && (
                            <div className="quota-limit-indicator"></div>
                        )}
                        {this.state.showSuggestions && this.state.suggestions.length > 0 && (
                            <div className="suggestions-dropdown">
                                {this.state.suggestions.map((suggestion, index) => (
                                    <div
                                        key={index}
                                        className="suggestion-item"
                                        onMouseDown={() => this.handleSuggestionClick(suggestion)}
                                    >
                                        {suggestion.a || suggestion.full_address || suggestion.address}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {this.props.showSubmitButton && (
                        <button className="search-submit-button" type="submit" aria-label="Search address">
                            <SearchIcon></SearchIcon>
                        </button>
                    )}
                </fieldset>
            </form>
        )
    }
}
